import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Wallet, Coins, Target, Trophy, BookOpen, Zap, Star, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, updateUser } = useAuth();
  const [missions, setMissions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const missionsRes = await axios.get(`${API}/missions`);
        const coursesRes = await axios.get(`${API}/courses`);
        setMissions(missionsRes.data.slice(0, 4));
        setCourses(coursesRes.data.slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const completeMission = async (id) => {
    try {
      const r = await axios.post(`${API}/missions/${id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`+${r.data.tokens_earned} AIH`);
      updateUser({ 
        aih_tokens: (user.aih_tokens || 0) + r.data.tokens_earned, 
        xp_points: (user.xp_points || 0) + r.data.xp_earned 
      });
      setMissions(missions.filter(m => m.id !== id));
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed'); 
    }
  };

  const level = Math.floor((user?.xp_points || 0) / 1000) + 1;
  const progress = ((user?.xp_points || 0) % 1000) / 10;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2">
            Welcome, <span className="gradient-text">{user?.username}</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <Coins size={20} className="text-[#D4AF37]" />
              </div>
              <span className="text-green-400 text-sm">+12%</span>
            </div>
            <p className="text-2xl font-unbounded font-bold text-white">{(user?.aih_tokens || 0).toLocaleString()}</p>
            <p className="text-sm text-white/60">AIH Tokens</p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Wallet size={20} className="text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-unbounded font-bold text-white">{(user?.staked_tokens || 0).toLocaleString()}</p>
            <p className="text-sm text-white/60">Staked AIH</p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Zap size={20} className="text-blue-400" />
              </div>
              <span className="text-blue-400 text-sm">Lv {level}</span>
            </div>
            <p className="text-2xl font-unbounded font-bold text-white">{(user?.xp_points || 0).toLocaleString()}</p>
            <p className="text-sm text-white/60">XP Points</p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Star size={20} className="text-green-400" />
              </div>
              <span className="text-green-400 text-sm">{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} className="mb-2 h-2" />
            <p className="text-sm text-white/60">To Level {level + 1}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="glass-panel p-6" data-testid="missions-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-unbounded text-xl font-bold text-white flex items-center gap-2">
                <Trophy size={20} className="text-[#D4AF37]" /> Missions
              </h2>
              <Link to="/community" className="text-[#D4AF37] text-sm hover:underline">View All</Link>
            </div>
            <div className="space-y-4">
              {missions.length === 0 && (
                <p className="text-white/60 text-center py-8">No active missions</p>
              )}
              {missions.map(m => (
                <div key={m.id} className={`mission-card ${m.mission_type}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`text-xs uppercase font-semibold ${
                        m.mission_type === 'daily' ? 'text-green-400' : 
                        m.mission_type === 'weekly' ? 'text-blue-400' : 'text-[#D4AF37]'
                      }`}>{m.mission_type}</span>
                      <h3 className="text-white font-semibold mb-1">{m.title}</h3>
                      <p className="text-sm text-white/60">{m.description}</p>
                      <div className="flex gap-4 mt-2">
                        <span className="text-xs text-[#D4AF37]">+{m.token_reward} AIH</span>
                        <span className="text-xs text-blue-400">+{m.xp_reward} XP</span>
                      </div>
                    </div>
                    <Button size="sm" className="btn-primary text-xs" onClick={() => completeMission(m.id)}>Claim</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel p-6" data-testid="courses-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-unbounded text-xl font-bold text-white flex items-center gap-2">
                <BookOpen size={20} className="text-[#D4AF37]" /> Courses
              </h2>
              <Link to="/academy" className="text-[#D4AF37] text-sm hover:underline">View All</Link>
            </div>
            <div className="space-y-4">
              {courses.map(c => (
                <Link key={c.id} to="/academy" className="block course-card p-4 hover:border-[#D4AF37]/30">
                  <div className="flex items-center gap-4">
                    <img src={c.image_url} alt={c.title} className="w-16 h-16 rounded-lg object-cover" />
                    <div className="flex-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        c.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' : 
                        c.difficulty === 'intermediate' ? 'bg-blue-500/20 text-blue-400' : 
                        'bg-purple-500/20 text-purple-400'
                      }`}>{c.difficulty}</span>
                      <h3 className="text-white font-semibold text-sm mt-1">{c.title}</h3>
                      <div className="flex gap-3 mt-1">
                        <span className="text-xs text-[#D4AF37]">+{c.token_reward} AIH</span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-white/40" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/wallet" className="glass-panel p-6 hover:border-[#D4AF37]/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <Wallet size={24} className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Manage Wallet</h3>
                <p className="text-sm text-white/60">Stake & view balance</p>
              </div>
            </div>
          </Link>

          <Link to="/calculator" className="glass-panel p-6 hover:border-[#D4AF37]/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Target size={24} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Calculator</h3>
                <p className="text-sm text-white/60">Calculate earnings</p>
              </div>
            </div>
          </Link>

          <Link to="/community" className="glass-panel p-6 hover:border-[#D4AF37]/30 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Trophy size={24} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Community</h3>
                <p className="text-sm text-white/60">Connect & earn</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
